/*
One place that records a failed backend call, for both request clients.

Why it exists: every backend error is rendered through a single friendly
sentence, which is right for the person and blind for us. "You need to be signed
in to do that." is the same message whether it came from /user_plan, /save_instance
or /can_user_save — so when a signed-out visitor could not run an example on
2026-08-28, the only way to learn WHICH call returned the 401 was to ask them to
reproduce it with the network tab open. The route was already on the error
object; it simply never left the browser.

Deliberately carries no request body, no response body and no user id — a route,
a status and an error code are enough to name the failing call, and anything more
would be logging a user's scenario data to a third party.
*/
import { track, EVENTS } from "utils/analytics.js";

/** Statuses the app causes on purpose and does not want to page itself about. */
const EXPECTED_STATUSES = new Set([
  404, // "no runs yet" reads as a 404 on several history routes
  409, // save conflicts, resolved in the UI
]);

/**
 * @param {string} route - the endpoint that failed, e.g. "user_plan"
 * @param {number} status - HTTP status
 * @param {string} [code] - our own error code, e.g. PLAN_LIMIT_EXCEEDED
 * @param {string} [method] - "POST" | "GET"
 */
export function recordRequestFailure(route, status, code, method) {
  if (EXPECTED_STATUSES.has(status)) return;
  try {
    track(EVENTS.REQUEST_FAILED, {
      route: route || "unknown",
      status: status || 0,
      code: code || null,
      method: method || null,
    });
  } catch {
    // Telemetry must never be the reason a request-error path throws: the caller
    // is already handling a failure and a second one would mask the first.
  }
  // Also in the console, because the person who hits this in dev is usually the
  // one who can fix it, and they should not have to wait for an analytics query.
  if (typeof console !== "undefined" && console.warn) {
    console.warn(`[api] ${method || "POST"} /${route} failed: ${status}${code ? ` (${code})` : ""}`);
  }
}

export default recordRequestFailure;
